import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowLeft,
  ArrowLeftRight,
  ArrowUpFromLine,
  Ban,
  MapPin,
  Pencil,
  UserRound,
} from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  StockAdjustmentReasons,
  StockBatch,
  StockBatchSource,
  StockItemDetail,
  StockCatalogueItem,
  StockLevels,
  StockLocation,
  StockOnOrder,
  StockTransactionItem,
} from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import {
  formatCount,
  formatDateOrDash,
  formatDateTimeOrDash,
} from "@shared-core/domain/formatters";
import {
  batchWithdrawalLabel,
  formatSignedQuantity,
  isInbound,
  reasonLabelLookup,
} from "@shared-core/domain/stock";
import { paths } from "@shared-core/routing/routes";
import { isFeatureEnabled } from "@shared-core/config/features";
import {
  useCanManageStock,
  useCanViewReplenishment,
} from "../../app/auth/hooks";
import { ListPagination } from "../../app/components/patterns/ListPagination";
import { Badge } from "../../app/components/primitives/badge";
import { Button } from "../../app/components/primitives/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../app/components/primitives/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import {
  type AdjustDirection,
  AdjustStockDialog,
  batchLabel,
} from "./AdjustStockDialog";
import { ChangeOwnerDialog } from "./ChangeOwnerDialog";
import { ReplenishmentPanel } from "./ReplenishmentPanel";
import { StockItemFormDialog } from "./StockItemFormDialog";
import { StockPicksCard } from "./StockPicksCard";
import {
  type PositionTarget,
  StockPositionDialog,
} from "./StockPositionDialog";
import { TransferStockDialog } from "./TransferStockDialog";
import { WithdrawBatchDialog } from "./WithdrawBatchDialog";

const HISTORY_PAGE_SIZE = 20;

export function StockItemPage() {
  const { stockItemId = "" } = useParams<{ stockItemId: string }>();
  const dataAdapter = useDataAdapter();
  const canManage = useCanManageStock();
  const canViewReplenishment = useCanViewReplenishment();
  const showReplenishment =
    isFeatureEnabled("stock_replenishment") && canViewReplenishment;

  const [item, setItem] = useState<StockItemDetail | null>(null);
  const [status, setStatus] = useState<
    "loading" | "ready" | "not_found" | "error"
  >("loading");
  const [reasons, setReasons] = useState<StockAdjustmentReasons | null>(null);
  const [history, setHistory] = useState<StockTransactionItem[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(0);
  const [historyError, setHistoryError] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);
  const [locations, setLocations] = useState<StockLocation[]>([]);
  /** Views' stock on order for this item: undefined while loading, null when nothing is on order. */
  const [onOrder, setOnOrder] = useState<StockOnOrder | null | undefined>(
    undefined,
  );
  const [onOrderError, setOnOrderError] = useState(false);
  /** Views' "delivered against" for each batch by id; undefined while loading or when it failed (the cell then reads the batch's own ids). */
  const [batchSources, setBatchSources] = useState<
    Record<string, StockBatchSource> | undefined
  >(undefined);
  const [batchSourcesVersion, setBatchSourcesVersion] = useState(0);
  const [olderArtworkBatchIds, setOlderArtworkBatchIds] = useState<string[]>(
    [],
  );
  const [adjusting, setAdjusting] = useState<AdjustDirection | null>(null);
  const [isTransferring, setIsTransferring] = useState(false);
  const [positionTarget, setPositionTarget] = useState<PositionTarget | null>(
    null,
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingOwner, setIsChangingOwner] = useState(false);
  const [withdrawing, setWithdrawing] = useState<StockBatch | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    dataAdapter
      .getStockItem(stockItemId)
      .then((detail) => {
        if (cancelled) return;
        setItem(detail);
        setStatus(detail ? "ready" : "not_found");
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error("stock.item.load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, stockItemId]);

  useEffect(() => {
    let cancelled = false;
    dataAdapter
      .getStockAdjustmentReasons()
      .then((result) => !cancelled && setReasons(result))
      .catch((error: unknown) => {
        console.error("stock.reasons.load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  useEffect(() => {
    let cancelled = false;
    dataAdapter
      .getStockBatchSources(stockItemId)
      .then(
        (result) =>
          !cancelled &&
          setBatchSources(
            Object.fromEntries(result.batches.map((b) => [b.batch_id, b])),
          ),
      )
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error("stock.batch_sources.load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
        setBatchSources(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, stockItemId, batchSourcesVersion]);

  useEffect(() => {
    let cancelled = false;
    setOnOrder(undefined);
    setOnOrderError(false);
    dataAdapter
      .listStockOnOrder({ stockItemRef: [stockItemId] })
      .then((page) => !cancelled && setOnOrder(page.items[0] ?? null))
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error("stock.on_order.load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
        setOnOrderError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, stockItemId]);

  useEffect(() => {
    let cancelled = false;
    dataAdapter
      .listStockLocations()
      .then((result) => !cancelled && setLocations(result))
      .catch((error: unknown) => {
        console.error("stock.locations.load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  useEffect(() => {
    let cancelled = false;
    setHistoryError(false);
    dataAdapter
      .listStockTransactions(stockItemId, {
        page: historyPage,
        pageSize: HISTORY_PAGE_SIZE,
      })
      .then((result) => {
        if (cancelled) return;
        setHistory(result.items);
        setHistoryTotalPages(result.totalPages);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error("stock.history.load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
        setHistoryError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, stockItemId, historyPage, historyVersion]);

  const refreshHistory = useCallback(() => {
    setHistoryPage(1);
    setHistoryVersion((v) => v + 1);
  }, []);

  /** After a movement: the new levels at once, then holdings and history re-read. */
  const afterMovement = useCallback(
    async (levels: StockLevels) => {
      setItem((current) => (current ? { ...current, levels } : current));
      refreshHistory();
      setBatchSourcesVersion((v) => v + 1);
      try {
        const detail = await dataAdapter.getStockItem(stockItemId);
        if (detail) setItem(detail);
      } catch (error) {
        console.error("stock.item.reload_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
      }
    },
    [dataAdapter, refreshHistory, stockItemId],
  );

  async function runAction(
    action: () => Promise<StockItemDetail | void>,
    failure: string,
  ) {
    setActionError(null);
    try {
      const result = await action();
      if (result) setItem(result);
      return true;
    } catch (error) {
      console.error("stock.item.action_failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      setActionError(failure);
      return false;
    }
  }

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Loading stock item…</div>
      </div>
    );
  }

  if (status !== "ready" || item == null) {
    return (
      <div className="space-y-4">
        <BackLink />
        <div
          role="alert"
          className="flex items-center gap-2 text-sm p-4 border border-border rounded-lg text-muted-foreground"
        >
          <AlertTriangle className="size-4 shrink-0" />
          {status === "not_found"
            ? "This stock item doesn't exist. It may have been mistyped or removed from a link."
            : "Couldn't load this stock item. Please try again."}
        </div>
      </div>
    );
  }

  const label = reasonLabelLookup(reasons);
  const canChange = canManage && !item.is_archived;
  const canMove = canChange;
  const hasStock = item.holdings.some(
    (h) => h.on_hand > 0 && h.location_is_active,
  );
  const { levels } = item;
  const withdrawnBatchIds = new Set(
    item.batches.filter((b) => b.withdrawn_at).map((b) => b.id),
  );

  return (
    <div className="space-y-6">
      <BackLink />

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl">{item.stock_code}</h1>
            {item.is_archived && <Badge variant="neutral">Archived</Badge>}
            {levels.needs_reorder && !item.is_archived && (
              <Badge variant="warning">Needs re-order</Badge>
            )}
            {item.tracks_batches && (
              <Badge variant="neutral">Tracks batches</Badge>
            )}
          </div>
          <p className="text-muted-foreground">{item.description}</p>
          <OwnerLine item={item} />
        </div>
        {canManage && (
          <div className="flex flex-wrap items-center gap-2">
            {canMove && (
              <>
                <Button
                  onClick={() => setAdjusting("in")}
                  disabled={reasons == null || locations.length === 0}
                >
                  <ArrowDownToLine className="size-4" />
                  Adjust in
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setAdjusting("out")}
                  disabled={reasons == null || !hasStock}
                >
                  <ArrowUpFromLine className="size-4" />
                  Adjust out
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsTransferring(true)}
                  disabled={!hasStock || locations.length < 2}
                >
                  <ArrowLeftRight className="size-4" />
                  Transfer
                </Button>
              </>
            )}
            {canChange && (
              <>
                <Button variant="outline" onClick={() => setIsEditing(true)}>
                  <Pencil className="size-4" />
                  Edit details
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsChangingOwner(true)}
                >
                  <UserRound className="size-4" />
                  Change owner
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              onClick={() =>
                void runAction(
                  () =>
                    item.is_archived
                      ? dataAdapter.restoreStockItem(item.id)
                      : dataAdapter.archiveStockItem(item.id),
                  item.is_archived
                    ? "Couldn't restore this item."
                    : "Couldn't archive this item.",
                )
              }
            >
              {item.is_archived ? "Restore" : "Archive"}
            </Button>
          </div>
        )}
      </div>

      {actionError && (
        <div
          role="alert"
          className="flex items-center gap-2 text-sm p-3 border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-300"
        >
          <AlertTriangle className="size-4 shrink-0" />
          {actionError}
        </div>
      )}

      {item.is_archived && (
        <div className="text-sm p-3 rounded-lg border border-border bg-surface text-muted-foreground">
          This stock item is archived. The stock item's history is kept, but the
          stock item can't be adjusted or edited until it's restored.
        </div>
      )}

      {levels.needs_reorder && !item.is_archived && (
        <div className="text-sm p-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 text-amber-900 dark:text-amber-200">
          Projected stock is at or below the re-order level of{" "}
          {formatCount(item.reorder_level ?? 0)}.
          {item.reorder_quantity != null && (
            <> Re-order quantity: {formatCount(item.reorder_quantity)}.</>
          )}
        </div>
      )}

      <section
        aria-label="Stock levels"
        className="grid gap-3 grid-cols-2 lg:grid-cols-4"
      >
        <LevelTile label="On hand" value={levels.on_hand} />
        <LevelTile
          label="On order"
          value={
            onOrder === undefined ? null : (onOrder?.on_order_quantity ?? 0)
          }
          muted
        />
        <LevelTile
          label="Pending despatch"
          value={levels.pending_despatch}
          muted
        />
        <LevelTile label="Projected" value={levels.projected} emphasis />
      </section>
      <p className="text-xs text-muted-foreground -mt-3">
        On order is every open order line delivering into this item, by any
        route (the card below). Pending despatch is the stock picked for
        despatch below. Projected is on hand less pending despatch. Projected
        doesn't count on order yet.
      </p>

      <OnOrderCard onOrder={onOrder} hasFailed={onOrderError} />

      {showReplenishment && (
        <ReplenishmentPanel
          stockItemId={item.id}
          onOlderArtworkBatchIds={setOlderArtworkBatchIds}
        />
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Warehouses</CardTitle>
          {canMove && (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setPositionTarget({
                  location_id: null,
                  batch_id: null,
                  position: null,
                })
              }
              disabled={locations.length === 0}
            >
              <MapPin className="size-4" />
              Record a bin
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {item.holdings.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Not held anywhere yet. Booking stock in at a warehouse, or
              recording a bin, adds it here.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Warehouse</TableHead>
                    {item.tracks_batches && <TableHead>Batch</TableHead>}
                    <TableHead>Bin</TableHead>
                    <TableHead className="text-right">On hand</TableHead>
                    {canMove && (
                      <TableHead className="sr-only">Actions</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {item.holdings.map((holding) => (
                    <TableRow key={holding.id}>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium">
                            {holding.location_name ?? holding.location_id}
                          </span>
                          {!holding.location_is_active && (
                            <Badge variant="neutral">Inactive</Badge>
                          )}
                        </div>
                      </TableCell>
                      {item.tracks_batches && (
                        <TableCell>
                          {holding.batch_no != null
                            ? batchLabel(
                                holding.batch_no,
                                holding.artwork_revision_id ?? null,
                              )
                            : "–"}
                          {holding.batch_id &&
                            withdrawnBatchIds.has(holding.batch_id) && (
                              <Badge variant="neutral" className="ml-2">
                                Withdrawn
                              </Badge>
                            )}
                        </TableCell>
                      )}
                      <TableCell
                        className={
                          holding.position ? undefined : "text-muted-foreground"
                        }
                      >
                        {holding.position ?? "–"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCount(holding.on_hand)}
                      </TableCell>
                      {canMove && (
                        <TableCell className="text-right">
                          {holding.location_is_active &&
                            !(
                              holding.batch_id &&
                              withdrawnBatchIds.has(holding.batch_id)
                            ) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() =>
                                  setPositionTarget({
                                    location_id: holding.location_id,
                                    batch_id: holding.batch_id ?? null,
                                    position: holding.position ?? null,
                                  })
                                }
                              >
                                {holding.position ? "Edit bin" : "Add bin"}
                              </Button>
                            )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {item.tracks_batches && (
        <Card>
          <CardHeader>
            <CardTitle>Batches</CardTitle>
          </CardHeader>
          <CardContent>
            {item.batches.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No batches yet. Each receipt booked in starts one.
              </p>
            ) : (
              <>
                <p className="text-sm text-muted-foreground mb-3">
                  One batch per receipt, oldest first. Book the older batch out
                  before the newer one unless the customer asks otherwise.
                </p>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Batch</TableHead>
                        <TableHead>Artwork revision</TableHead>
                        <TableHead>Delivered against</TableHead>
                        <TableHead>Goods-in</TableHead>
                        <TableHead>Received</TableHead>
                        <TableHead className="text-right">On hand</TableHead>
                        <TableHead>Status</TableHead>
                        {canChange && (
                          <TableHead className="sr-only">Actions</TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {item.batches.map((batch) => (
                        <TableRow key={batch.id}>
                          <TableCell>{batch.batch_no}</TableCell>
                          <TableCell>
                            {batch.artwork_revision_id ?? "–"}
                            {olderArtworkBatchIds.includes(batch.id) && (
                              <Badge variant="warning" className="ml-2">
                                Older artwork
                              </Badge>
                            )}
                            {batch.valid_until && (
                              <span className="block text-xs text-muted-foreground">
                                Valid until{" "}
                                {formatDateOrDash(batch.valid_until)}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <DeliveredAgainst
                              batch={batch}
                              source={batchSources?.[batch.id]}
                            />
                          </TableCell>
                          <TableCell>{batch.goods_in_ref ?? "–"}</TableCell>
                          <TableCell className="whitespace-nowrap">
                            {formatDateTimeOrDash(batch.created_at)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatCount(
                              item.holdings
                                .filter((h) => h.batch_id === batch.id)
                                .reduce((sum, h) => sum + h.on_hand, 0),
                            )}
                          </TableCell>
                          <TableCell>
                            <BatchStatus batch={batch} />
                          </TableCell>
                          {canChange && (
                            <TableCell className="text-right">
                              {!batch.withdrawn_at && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setWithdrawing(batch)}
                                >
                                  <Ban className="size-4" />
                                  Withdraw
                                </Button>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
            <div>
              <dt className="text-muted-foreground">
                Repeatable catalogue item
              </dt>
              <dd>
                <CatalogueLink
                  catalogueItemId={item.repeatable_catalogue_item_id}
                  catalogueItem={item.repeatable_catalogue_item}
                />
              </dd>
            </div>
            <Detail term="Size" value={item.size ?? "–"} />
            <Detail term="Units" value={formatCount(item.unit_quantity)} />
            <Detail
              term="Quantity in pack"
              value={formatCount(item.pack_quantity)}
            />
            <Detail
              term="No. boxes per pallet"
              value={
                item.boxes_per_pallet == null
                  ? "–"
                  : formatCount(item.boxes_per_pallet)
              }
            />
            <Detail
              term="Re-order level / quantity"
              value={`${item.reorder_level == null ? "–" : formatCount(item.reorder_level)} / ${
                item.reorder_quantity == null
                  ? "–"
                  : formatCount(item.reorder_quantity)
              }`}
            />
          </dl>
        </CardContent>
      </Card>

      {item.owner_changes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Owner history</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {item.owner_changes.map((change) => (
                    <TableRow key={change.id}>
                      <TableCell className="whitespace-nowrap">
                        {formatDateTimeOrDash(change.changed_at)}
                      </TableCell>
                      <TableCell>{ownerName(change.from_owner_name)}</TableCell>
                      <TableCell>{ownerName(change.to_owner_name)}</TableCell>
                      <TableCell>{change.reason}</TableCell>
                      <TableCell>{change.changed_by_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <StockPicksCard stockItemId={item.id} reloadKey={historyVersion} />

      <Card>
        <CardHeader>
          <CardTitle>History</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {historyError ? (
            <p role="alert" className="text-sm text-red-700 dark:text-red-300">
              Couldn't load the history. Please try again.
            </p>
          ) : history.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No movements yet. Book opening stock in with Adjust in, using the
              Opening balance reason.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead className="text-right">Movement</TableHead>
                    <TableHead>Warehouse</TableHead>
                    {item.tracks_batches && <TableHead>Revision</TableHead>}
                    <TableHead>Reason</TableHead>
                    <TableHead>Note</TableHead>
                    <TableHead className="text-right">There after</TableHead>
                    <TableHead className="text-right">Total after</TableHead>
                    <TableHead>By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell className="whitespace-nowrap">
                        {formatDateTimeOrDash(transaction.created_at)}
                      </TableCell>
                      <TableCell
                        className={`text-right tabular-nums font-medium ${
                          isInbound(transaction.transaction_type)
                            ? "text-green-700 dark:text-green-400"
                            : "text-red-700 dark:text-red-400"
                        }`}
                      >
                        {formatSignedQuantity(transaction)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {transaction.location_name ?? transaction.location_id}
                      </TableCell>
                      {item.tracks_batches && (
                        <TableCell>
                          {transaction.artwork_revision_id ?? "–"}
                        </TableCell>
                      )}
                      <TableCell>
                        {movementReason(transaction, label)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {transaction.note ?? "–"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {formatCount(transaction.location_balance_after)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCount(transaction.balance_after)}
                      </TableCell>
                      <TableCell>{transaction.created_by_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <ListPagination
            page={historyPage}
            totalPages={historyTotalPages}
            onPageChange={setHistoryPage}
          />
        </CardContent>
      </Card>

      {adjusting && reasons && (
        <AdjustStockDialog
          item={item}
          direction={adjusting}
          reasons={reasons}
          locations={locations}
          onClose={() => setAdjusting(null)}
          onAdjusted={(result) => {
            setAdjusting(null);
            void afterMovement(result.levels);
          }}
        />
      )}
      {isTransferring && (
        <TransferStockDialog
          item={item}
          locations={locations}
          onClose={() => setIsTransferring(false)}
          onTransferred={(result) => {
            setIsTransferring(false);
            void afterMovement(result.levels);
          }}
        />
      )}
      {positionTarget && (
        <StockPositionDialog
          item={item}
          target={positionTarget}
          locations={locations}
          onClose={() => setPositionTarget(null)}
          onSaved={(saved) => {
            setPositionTarget(null);
            setItem(saved);
          }}
        />
      )}
      {isEditing && (
        <StockItemFormDialog
          item={item}
          onClose={() => setIsEditing(false)}
          onSaved={(saved) => {
            setIsEditing(false);
            setItem(saved);
          }}
        />
      )}
      {withdrawing && (
        <WithdrawBatchDialog
          item={item}
          batch={withdrawing}
          onClose={() => setWithdrawing(null)}
          onWithdrawn={(saved) => {
            setWithdrawing(null);
            setItem(saved);
            refreshHistory();
          }}
        />
      )}
      {isChangingOwner && (
        <ChangeOwnerDialog
          item={item}
          onClose={() => setIsChangingOwner(false)}
          onSaved={(saved) => {
            setIsChangingOwner(false);
            setItem(saved);
          }}
        />
      )}
    </div>
  );
}

/** A transfer line names where the stock went or came from; a book-out, its
 * despatch; an external movement, the other system's reference; an
 * adjustment, its reason. */
function movementReason(
  transaction: StockTransactionItem,
  label: (reason: string) => string,
): string {
  switch (transaction.transaction_type) {
    case "transfer_out":
      return `Transfer to ${transaction.counterpart_location_name ?? "?"}`;
    case "transfer_in":
      return `Transfer from ${transaction.counterpart_location_name ?? "?"}`;
    case "book_out":
      return `Despatched (${transaction.despatch_ref ?? "?"})`;
    case "external_in":
    case "external_out":
      return `External movement (${transaction.external_ref ?? "?"})`;
    default:
      return transaction.reason ? label(transaction.reason) : "–";
  }
}

/** The owner and the billing account that pays for the stock; the same line
 * for every owner, with the account's kind (customer or internal). */
function OwnerLine({ item }: { item: StockItemDetail }) {
  const owner = item.owner;
  return (
    <p className="text-sm">
      Owned by{" "}
      <span className="font-medium">
        {owner?.name ?? "a customer no longer active"}
      </span>{" "}
      <span className="text-muted-foreground">
        {owner
          ? `(${owner.customer_code}, ${
              owner.billing_account_ref
                ? `purchase invoices to ${owner.billing_account_ref}${
                    owner.billing_account_kind === "internal"
                      ? ", internal account"
                      : ""
                  }`
                : "no billing account yet"
            })`
          : ""}
      </span>
    </p>
  );
}

function ownerName(name: string | null | undefined): string {
  return name ?? "A customer no longer active";
}

/** Live, or withdrawn: when, why, and the note a person gave. */
function BatchStatus({ batch }: { batch: StockBatch }) {
  if (!batch.withdrawn_at || !batch.withdrawn_reason) {
    return <span className="text-muted-foreground">In stock</span>;
  }
  return (
    <div className="space-y-0.5">
      <Badge variant="neutral">Withdrawn</Badge>
      <p className="text-xs text-muted-foreground">
        {batchWithdrawalLabel(batch.withdrawn_reason)} ·{" "}
        {formatDateTimeOrDash(batch.withdrawn_at)}
      </p>
      {batch.withdrawn_note && (
        <p className="text-xs text-muted-foreground">{batch.withdrawn_note}</p>
      )}
    </div>
  );
}

/** Where a batch came from: the order line it was delivered against, and the
 * supplier's purchase order or the in-house Tharstern job behind it. Views
 * names them by reference number, linked to the order and the purchase order;
 * until it answers (or when it cannot) the batch's own ids stand in. */
function DeliveredAgainst({
  batch,
  source,
}: {
  batch: StockBatch;
  source: StockBatchSource | undefined;
}) {
  if (!batch.order_line_id) return <>Import or adjustment</>;
  if (!source?.line_ref || !source.order_no) return <>{receiptSource(batch)}</>;
  const orderNo = source.order_no;
  return (
    <span>
      <Link
        className="underline-offset-2 hover:underline"
        to={paths.orderDetail({
          orderNo,
          step: "order",
          line: source.line_ref.slice(orderNo.length),
        })}
      >
        {source.line_ref}
      </Link>
      {source.purchase_order_no ? (
        <>
          {" · "}
          <Link
            className="underline-offset-2 hover:underline"
            to={paths.purchaseOrderDetail({
              purchaseOrderNo: source.purchase_order_no,
            })}
          >
            {source.purchase_order_no}
          </Link>
        </>
      ) : source.tharstern_job_ref ? (
        <> · job {source.tharstern_job_ref}</>
      ) : null}
    </span>
  );
}

/** A batch's own ids, where views has not named them. */
function receiptSource(batch: {
  order_line_id?: string | null;
  purchase_order_line_id?: string | null;
  tharstern_job_ref?: string | null;
}): string {
  if (!batch.order_line_id) return "Import or adjustment";
  if (batch.purchase_order_line_id)
    return `${batch.order_line_id} · PO line ${batch.purchase_order_line_id}`;
  if (batch.tharstern_job_ref)
    return `${batch.order_line_id} · job ${batch.tharstern_job_ref}`;
  return batch.order_line_id;
}

/** A linked catalogue item, an unlinked dash, or a link whose item has gone. */
function CatalogueLink({
  catalogueItemId,
  catalogueItem,
}: {
  catalogueItemId?: string | null;
  catalogueItem?: StockCatalogueItem | null;
}) {
  if (catalogueItem)
    return (
      <span>
        {catalogueItem.name}
        <span className="text-muted-foreground">
          {" "}
          · {catalogueItem.catalogue_name}
        </span>
      </span>
    );
  if (catalogueItemId)
    return (
      <span className="text-amber-700 dark:text-amber-300">
        No longer in the catalogue
      </span>
    );
  return <span className="text-muted-foreground">Not linked</span>;
}

function BackLink() {
  return (
    <Link
      to={paths.stock()}
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="size-4" />
      Stock
    </Link>
  );
}

/** The open order lines delivering into the item, from views' stock on order. */
function OnOrderCard({
  onOrder,
  hasFailed,
}: {
  onOrder: StockOnOrder | null | undefined;
  hasFailed: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>On order</CardTitle>
      </CardHeader>
      <CardContent>
        {hasFailed ? (
          <p role="alert" className="text-sm text-muted-foreground">
            Couldn't load what is on order for this item.
          </p>
        ) : onOrder === undefined ? (
          <p className="text-sm text-muted-foreground">Loading on order…</p>
        ) : onOrder === null ? (
          <p className="text-sm text-muted-foreground">
            Nothing on order. An order line delivering into this item, bought or
            made, shows here until it arrives.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order line</TableHead>
                  <TableHead>Route</TableHead>
                  <TableHead>Purchase order</TableHead>
                  <TableHead>Required</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {onOrder.lines.map((line) => (
                  <TableRow key={line.line_ref}>
                    <TableCell>
                      <Link
                        className="underline-offset-2 hover:underline"
                        to={paths.orderDetail({
                          orderNo: line.order_no,
                          step: "order",
                          line: line.line_ref.slice(line.order_no.length),
                        })}
                      >
                        {line.line_ref}
                      </Link>
                    </TableCell>
                    <TableCell>{ROUTE_LABELS[line.route ?? "none"]}</TableCell>
                    <TableCell>
                      {line.purchase_order_no ? (
                        <Link
                          className="underline-offset-2 hover:underline"
                          to={paths.purchaseOrderDetail({
                            purchaseOrderNo: line.purchase_order_no,
                          })}
                        >
                          {line.purchase_order_no}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDateOrDash(line.required_date)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCount(line.outstanding_quantity)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const ROUTE_LABELS: Record<string, string> = {
  buy: "Bought",
  make: "Made",
  from_stock: "From stock",
  none: "—",
};

function LevelTile({
  label,
  value,
  muted = false,
  emphasis = false,
}: {
  label: string;
  /** Null while loading. */
  value: number | null;
  muted?: boolean;
  emphasis?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border p-4 ${emphasis ? "border-primary/40 bg-primary/5" : "border-border bg-surface"}`}
    >
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={`text-2xl tabular-nums ${muted ? "text-muted-foreground" : ""}`}
      >
        {value === null ? "…" : formatCount(value)}
      </div>
    </div>
  );
}

function Detail({ term, value }: { term: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground">{term}</dt>
      <dd>{value}</dd>
    </div>
  );
}
